import {
  instanceOfIExternalDataApplicable,
  IExternalScriptExecutable,
  instanceOfIExternalScriptExecutable,
  ExternalSubItem,
  IItem,
  RelationalActionParameter,
  instanceOfIExternalScriptAssociatable,
  ExternalDataItem,
  ExternalProcessItem,
  instanceOfIExternalScriptImageAssociatable,
  instanceOfISubNameSpecifiable,
} from "./items";
import {
  ExecuteRequest,
  CellInformation,
  ScriptLinkItem,
  ExternalScriptExecuteResult,
  BatchRequest,
  BatchResult,
  ExternalTemplateCopyResult,
} from "../localhost/items";
import {
  RelationalActionKinds,
  ExternalScriptParameter,
  Section,
  ItemSet,
} from ".";
import { Dictionary, DateFormatter } from "../system";
import { DomainRegistry } from "..";
import { Sex } from "../patient";

// TODO: refactor
interface ItemSelectable {
  name: string;
  id: string;
  isHisInfo: boolean;
  groupKey: string;
  itemName: string;
  subName: string;
}

export class ExternalScriptManager {
  constructor(private _itemSet: ItemSet, private _sections: Section[]) {
    // itemSetIdが対応しているItemSetとSection[]が前提
  }

  public getAdditionalParameter(source: IExternalScriptExecutable, param: ExternalScriptParameter): ExecuteRequest {
    // TODO: 外部処理ではsourceSectionの判定は不要ではある（他のオブジェクトからパラメータを集める必要が無い）
    const sourceSection = this.getSectionFromPrivateId(source.privateId);
    const item = !!source.exePath
      ? source
      : this.findExternalScriptExecutableBaseItem(source.name, sourceSection);
    if (!item) { throw new Error("base item not found!!!"); }
    const request = new ExecuteRequest();
    request.patientId = param.patient.id;
    request.department = param.department;
    request.exePath = item.exePath;
    request.csvPath = item.csvPath;
    request.xmlValuePath = item.xmlValuePath;
    request.csvBeforeValuePath = item.csvBeforeValuePath;
    request.csvInputValuePath = item.csvInputValuePath;
    request.arguments = this.createArguments(item, param);
    request.splitter = item.splitter;
    request.values = this.getObjectValues(source.name, sourceSection);
    request.scriptLinkItems = this.createScriptLinkItems(item.actions);
    request.previousCompressedCsvText = item.previousCsvBinary;
    return request;
  }

  private getSectionFromPrivateId(privateId: number): Section {
    if (privateId == null) { return undefined; }

    for (const section of this._sections) {
      if (this.hasItem(section, privateId)) { return section; }
    }
    return undefined;
  }

  private hasItem(section: Section, privateId: number): boolean {
    return !!section && !!section.itemPrivateIds.find(id => id === privateId);
  }

  /**
   * 外部データは大本のオブジェクトにしか動作に必要な属性が指定されていない模様
   */
  private findExternalScriptExecutableBaseItem(name: string, sourceSection: Section): IExternalScriptExecutable {
    for (const item of this._itemSet.items) {
      if (!instanceOfIExternalScriptExecutable(item)) { continue; }
      if (!this.hasItem(sourceSection, item.privateId)) { continue; }
      if (ExternalScriptManager.isExternalScriptExecutableBaseItem(item, name)) { return item; }
    }
    return null;
  }

  public static isExternalScriptExecutableBaseItem(item: IItem, name: string): boolean {
    if (!instanceOfIExternalScriptExecutable(item)) { return false; }
    if (item.name !== name) { return false; }
    return !!item.exePath;
  }

  private getObjectValues(name: string, sourceSection: Section): CellInformation[] {
    // TODO: 外部処理契機の場合は抽出しなくて良さそう
    const values: CellInformation[] = [];
    for (const item of this._itemSet.items) {
      if (!item) { continue; }
      if (!instanceOfIExternalDataApplicable(item)) { continue; }
      if (item.name !== name) { continue; }
      if (!this.hasItem(sourceSection, item.privateId)) { continue; }
      const value = new CellInformation();
      value.row = item.row;
      value.column = item.column;
      value.value = item.getValueForExternalScript();
      values.push(value);
    }
    return values;
  }

  private createArguments(item: IExternalScriptExecutable, param: ExternalScriptParameter): string[] {
    return item.items.map(i => this.getArgumentText(i, param));
  }

  private getArgumentText(subItem: ExternalSubItem, param: ExternalScriptParameter): string {
    return subItem.isVariable
      ? this.getArgumentVariableText(subItem.key, param)
      : subItem.value;
  }

  private getArgumentVariableText(key: string, param: ExternalScriptParameter): string {
      if (key === "UserName") {
        if (!param.user || !param.user.name) { throw new Error("職員の氏名の値が格納されていません。"); }
        return param.user.name;
      }
      if (key === "UserID") {
        if (!param.user || !param.user.id) { throw new Error("職員IDの値が格納されていません。"); }
        return param.user.id;
      }
      if (key === "InternalUserID") {
        throw new Error("ユーザ内部IDの変換には対応していません。");
      }
      if (key === "PatientNo") {
        if (!param.patient || !param.patient.id) { throw new Error("患者IDの値が格納されていません。"); }
        return param.patient.id;
      }
      if (key === "PatientNo-NonCheck") {
        if (!param.patient || !param.patient.id) { return ""; }
        return param.patient.id;
      }
      if (key === "PatientID") {
        // 患者内部ID(DocuMaker採番)
        // 内部IDは非対応としてErrorを投げたいが、徳大で使用してしまっているフォーマットがあり、対応せざるをえない
        console.warn("V1仕様としては患者内部IDと置換するキーワードですが、外部IDで置換しています。");
        if (!param.patient || !param.patient.id) { throw new Error("患者IDの値が格納されていません。"); }
        return param.patient.id;
      }
      if (key === "PatientName") {
        if (!param.patient || !param.patient.name) { throw new Error("患者氏名の値が格納されていません。"); }
        return param.patient.name;
      }
      if (key === "PatientKana") {
        if (!param.patient || !param.patient.kanaName) { throw new Error("患者カナ氏名の値が格納されていません。"); }
        return param.patient.kanaName;
      }
      if (key === "PatientBirth") {
        if (!param.patient.birthday) { throw new Error("患者の生年月日の値が格納されていません。"); }
        return DateFormatter.format(param.patient.birthday, "YYYYMMDD");
      }
      if (key === "PatientSex") {
        if (!param.patient.sex) { throw new Error("患者の性別の値が格納されていません。"); }
        return this.toPatientSecText(param.patient.sex);
      }
      if (key === "MedicalDept") {
        if (!param.department || !param.department.id) {
          // Errorをthrowするようにしていたが、北大でHisDataPickerが起動できない差異があり、空文字を返却するようにしている。
          // ※V1の挙動を確認してみると空文字にしている
          // throw new Error("診療科が指定されていません。");
          return "";
        }
        return param.department.id;
      }
      if (key === "MedicalDept-NonCheck") {
        if (!param.department || !param.department.id) { return ""; }
        return param.department.id;
      }
      if (key === "ReportOrderNum-NonCheck") {
        return (param.orderNo !== null && param.orderNo !== undefined) ? param.orderNo : "";
      }
      if (key === "ScriptParameter-NonCheck") {
        const scriptParameter = this.extractScriptParameter(param.hisInfoGetterParameter);
        return !!scriptParameter ? scriptParameter : "";
      }
      if (key === "FormatId") {
        return param.formatId.toString();
      }
      if (key === "%CSV_PATH%" || key === "%BEFORE_VALUE_PATH%" || key === "%VALUE_PATH%" || key === "%INPUT_PATH%") {
        // フルパスに直す必要があるので、アシスタント側でリプレースする
        return key;
      }
      throw new Error("unknown item key!!! key: " + key);
  }

  private extractScriptParameter(hisInfoGetterParameter: string): string {
    if (!hisInfoGetterParameter) { return null; }

    for (const pair of hisInfoGetterParameter.split(",")) {
      const splitted = pair.split("=");
      if (splitted.length !== 2) { continue; }
      if (splitted[0].toUpperCase() === "SCRIPTPARAMETER") { return splitted[1]; }
    }
    return null;
  }

  private toPatientSecText(sex: Sex): string {
    if (sex === Sex.Male) { return "男"; }
    if (sex === Sex.Female) { return "女"; }
    // TODO:V1の仕様の確認
    return "不明";
  }

  private createScriptLinkItems(actions: RelationalActionParameter[]): ScriptLinkItem[] {
    if (!actions) { return []; }
    const items: ScriptLinkItem[] = [];
    for (const action of actions) {
      if (action.kind !== RelationalActionKinds.ScriptLinkInput) { continue; }
      items.push(this.createScriptLinkItem(action));
    }
    return items;
  }

  private createScriptLinkItem(action: RelationalActionParameter): ScriptLinkItem {
    const found = this.findRelatedItem(action);
    const value = instanceOfIExternalScriptAssociatable(found)
      ? found.getValueForExternalScript()
      : "";
    const claioImageId = instanceOfIExternalScriptImageAssociatable(found)
      ? (found.claioImageId ? found.claioImageId : "")  // nullだと画像を入れる枠の要素を作成しないので注意
      : null;

    const externalImageId = instanceOfIExternalScriptImageAssociatable(found)
      ? (found.externalImageId ? found.externalImageId : "")
      : null;
    
    // サブ名称はフォーマットの時点で決まっているように思うが、relational要素(RelationalActionParameter)では指定されない模様
    const subName = instanceOfISubNameSpecifiable(found)
      ? (found.subName ? found.subName : "")
      : null;

    const item = new ScriptLinkItem();
    item.name = action.key;
    item.subName = subName;
    item.value = value;
    item.objectId = action.objectId;
    item.itemName = action.subKey;
    item.claioImageId = claioImageId;
    item.externalImageId = externalImageId;
    return item;
  }

  private findRelatedItem(param: RelationalActionParameter): IItem {
    // 徳大はpageKeyがnullになっているので注意すること
    const specifiedSection = this._sections
      .filter(s => (s.pageKey != null) && s.pageKey === param.pageKey)
      .pop();
    
    if (specifiedSection) {
      const sectionItems = specifiedSection.itemPrivateIds.map(privateId => this._itemSet.items[privateId]);
      const found = this.findRelatedItemFromItems(param, sectionItems);
      if (found) { return found; }
    }
    // 指定されたpageKeyに見つからなかったら全ページから探索する
    return this.findRelatedItemFromItems(param, this._itemSet.items);
  }

  private findRelatedItemFromItems(param: RelationalActionParameter, items: IItem[]): IItem {
    for (const item of items) {
      if (!instanceOfIExternalScriptAssociatable(item) && !instanceOfIExternalScriptImageAssociatable(item)) { continue; }
      // サブ名称はフォーマットの時点で決まっているように思うが、relational要素(RelationalActionParameter)では指定されない模様
      if (!ExternalScriptManager.isMatch(param.objectId, param.key, param.subKey, null, item)) { continue; }
      return item;
    }
    return null;
  }

  public static isMatch(objectId: string, name: string, itemName: string, subName: string, item: any): boolean {
    // TODO:V1の仕様の確認
    // TODO:どういう判定しているのかフォーマットを見ただけでは分からないので、V1のソースコードを見る必要がありそう
    if (!item) { return false; }
    const selectable = item as ItemSelectable;
    if (objectId !== null && objectId !== undefined) {
      if (objectId !== selectable.id) { return false; }
    }
    if (name) {
      if (selectable.isHisInfo) {
        if (name !== selectable.groupKey) { return false; }  
      } else {
        if (name !== selectable.name) { return false; }  
      }
    }
    if (itemName) {
      if (itemName !== selectable.itemName) { return false; }
    }
    if (subName) {
      if (subName !== selectable.subName) { return false; }
    }
    return true;
  }

  public createUrlParameter(item: IExternalScriptExecutable): Dictionary<string, string> {
    // action以外も渡すようにするときは、Batch処理のほうも修正が必要なので注意が必要
    const parameter = new Dictionary<string, string>();
    parameter.set("action", this.getActionText(item));
    return parameter;
  }

  private getActionText(item: IExternalScriptExecutable): string {
    if (item instanceof ExternalDataItem) { return "externalData"; }
    if (item instanceof ExternalProcessItem) { return "externalProcess"; }
    console.warn("unknown external script!!!");
    return "externalData";
  }

  public async executeScript(param: ExternalScriptParameter, item: IExternalScriptExecutable): Promise<ExternalScriptExecuteResult> {
    const additionalParameter = this.getAdditionalParameter(item, param);
    const dictionary = this.createUrlParameter(item);
    const service = DomainRegistry.localhost.getApplicationService();
    const result = await service.launchResponsively(null, dictionary, additionalParameter);
    if (result instanceof ExternalScriptExecuteResult) {
      return result;
    }
    throw new Error("not implemented");
  }

  public async executeScripts(param: ExternalScriptParameter, items: IExternalScriptExecutable[]): Promise<BatchResult[]> {
    const requests = items.map(i => this.createBatchRequest(param, i));
    const service = DomainRegistry.localhost.getApplicationService();
    return service.launchAsBatch(requests);
  }

  private createBatchRequest(param: ExternalScriptParameter, item: IExternalScriptExecutable): BatchRequest {
    const request = new BatchRequest();
    request.source = item;
    request.action = this.getActionText(item);
    request.request = this.getAdditionalParameter(item, param);
    return request;
  }

  static async copyMksTemplateId(parameter: ExternalScriptParameter, mksTemplateRegisteredId: string): Promise<string> {
    const parameters = new Dictionary<string, string>();
    parameters.set("action", "mksTemplateCopy");
    parameters.set("patientId", parameter.patient.id);
    parameters.set("mksId", mksTemplateRegisteredId);

    const service = DomainRegistry.localhost.getApplicationService();
    const result = await service.launchResponsively(null, parameters, null);
    if (!result || !result.isComplete) {
      throw new Error("テンプレートのコピーに失敗しています。");
    }
    if (!(result instanceof ExternalTemplateCopyResult)) {
      throw new Error("result type mismatch!!!");
    }
    return result.copiedId;
  }
}
